import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Inject,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { UserService } from 'src/app/core/services/api/user.service';
import { SearchNgSelectService } from 'src/app/core/services/ui/search-ng-select.service';
import { USER_TYPE } from 'src/app/core/helpers/global/global.constant';
import { NG_SELECT_QUERIES } from 'src/app/core/helpers/ui/ui.constant';
import {
  NgSelect,
  NgSelectQuery,
} from 'src/app/core/interfaces/ui/ui.interface';
import {
  UserNgSelectData,
  UserNgSelect,
} from 'src/app/core/interfaces/api/user.interface';
import {
  ApiResponse,
  ApiData,
} from 'src/app/core/interfaces/api/api-response.interface';

@Component({
  selector: 'app-selects-users',
  templateUrl: './selects-users.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectsUsersComponent),
      multi: true,
    },
    { provide: 'ngUsers', useClass: SearchNgSelectService },
  ],
})
export class SelectsUsersComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = '';
  @Input() userType: string = '';
  @Input() label: string = '';
  @Input() clearable: boolean = true;
  @Input() disabled: boolean = false;
  @Input() searchable: boolean = true;
  @Input() from: string = '';
  @Input() excludedType: string | string[] = '';
  @Input() valueField: 'id' | 'username' = 'id';

  @Output() userSelected = new EventEmitter<string | null>();
  @Output() userCleared = new EventEmitter<void>();

  public users$: Observable<NgSelect<string>[]> = of([]);
  public NG_SELECT_QUERIES = NG_SELECT_QUERIES;
  public selectedValue: string | null = null;

  private onChange = (value: string | null) => {};
  private onTouched = () => {};

  constructor(
    @Inject('ngUsers') private _ngUsers: SearchNgSelectService<string>,
    private _userService: UserService,
    private _changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setConfigNgSelect();
  }

  private setConfigNgSelect(): void {
    this._ngUsers.setSearchTermKey('username');
    this._ngUsers.extendFilter({ type: this.userType });

    const fetchDataFunction = (
      filter: any
    ): Observable<ApiData<NgSelect<string>[]>> => {
      const userFilter: UserNgSelect = {
        user: filter.username || '',
        userType: (filter.userType = this.userType),
        ...filter,
      };

      if (this.from) {
        userFilter.from = this.from;
      }

      if (this.excludedType) {
        userFilter.excludedType = this.excludedType;
      }

      return this._userService
        .getAllUsersNgSelect(userFilter, filter.page || 1, filter.limit || 10)
        .pipe(
          map((response: ApiResponse<UserNgSelectData>) => ({
            totalCount: response.data.totalCount,
            result: response.data.result.map((user) => ({
              label: user.username || '',
              value: this.valueField === 'username' ? user.username || '' : user._id || '',
              currencies: [],
              currencyCode: null,
            })),
          }))
        );
    };

    this._ngUsers.setFetchDataFunction(fetchDataFunction);
    this.users$ = this._ngUsers.getData();
    this._ngUsers.triggerFetchData();
  }

  public onSearchSelect(term?: string): void {
    this._ngUsers.searchTerm(term || '');
  }

  public onScrollToEndSelect(): void {
    this._ngUsers.scrollToEnd();
  }

  public onUserChange(userId: string | null): void {
    this.selectedValue = userId;
    this.onChange(userId);
    this.onTouched();
    this.userSelected.emit(userId);
    this._changeDetector.markForCheck();
  }

  public onUserClear(): void {
    this.selectedValue = null;
    this.onChange(null);
    this.onTouched();
    this.userCleared.emit();
    this._changeDetector.markForCheck();
  }

  writeValue(value: string | null): void {
    this.selectedValue = value;
    this._changeDetector.markForCheck();
  }

  registerOnChange(filters: (value: string | null) => void): void {
    this.onChange = filters;
  }

  registerOnTouched(filters: () => void): void {
    this.onTouched = filters;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._changeDetector.markForCheck();
  }
}
